<template>
  <div class="interview-results">
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border" role="status" />
      <p class="mt-2 text-muted">Loading results…</p>
    </div>

    <div v-else-if="error" class="alert alert-danger">{{ error }}</div>

    <div v-else-if="interview">
      <div class="d-flex justify-content-between align-items-start mb-4">
        <h2 class="mb-1">{{ interview.tradeId }}</h2>
        <div
          v-if="interview.evaluationStatus !== 'pending'"
          class="score-badge text-center"
        >
          <div class="score-value">{{ totalScoreDisplay }}</div>
          <div class="score-label">/ 10</div>
        </div>
      </div>

      <!-- Pending evaluation — show confirmation, don't make user wait -->
      <div
        v-if="interview.evaluationStatus === 'pending'"
        class="text-center py-5"
      >
        <p class="fw-semibold fs-5 mb-2">Your interview has been submitted!</p>
        <p class="text-muted mb-4">
          Your responses have been recorded and are being reviewed.
        </p>
        <DefaultButton label-text="Continue" @click="navigateBack" />
      </div>

      <!-- Evaluation failed -->
      <div
        v-else-if="interview.evaluationStatus === 'failed'"
        class="alert alert-warning"
      >
        Scoring could not be completed for this interview. Please contact
        support.
      </div>

      <!-- Results ready -->
      <template v-else>
        <!-- AI Summary -->
        <div class="card mb-4">
          <div class="card-header fw-semibold">AI Summary</div>
          <div class="card-body">
            <p class="mb-0" style="white-space: pre-line">
              {{ interview.aiSummary }}
            </p>
          </div>
        </div>

        <!-- Per-question scores -->
        <h5 class="mb-3">Question Breakdown</h5>
        <div
          v-for="(question, index) in interview.questions"
          :key="question.id"
          class="question-result mb-3"
        >
          <div class="d-flex justify-content-between align-items-start">
            <div class="flex-grow-1 me-3">
              <p class="fw-semibold mb-1">
                {{ index + 1 }}.
                <span class="badge bg-secondary me-1">{{
                  question.category
                }}</span>
                {{ question.text }}
              </p>
              <p class="text-muted small mb-1">
                <em>{{ getResponse(question.id) }}</em>
              </p>
              <p class="text-muted small mb-0">
                {{ getRationale(question.id) }}
              </p>
              <p
                v-if="getTimeSpent(question.id)"
                class="text-muted small mb-0 mt-1"
              >
                Time spent: {{ formatTime(getTimeSpent(question.id)) }}
              </p>
            </div>
            <div class="score-pill">
              {{ getScore(question.id) }}<span>/10</span>
            </div>
          </div>
        </div>
      </template>

      <slot name="actions" />
    </div>
  </div>
</template>

<script>
import interviewApi from "@/api/interview"
import DefaultButton from "@/components/DefaultButton.vue"

export default {
  name: "InterviewResults",
  components: { DefaultButton },

  props: {
    interviewId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      interview: null,
      loading: true,
      error: null,
    }
  },

  computed: {
    totalScoreDisplay() {
      if (!this.interview?.totalScore) return "—"
      return this.interview.totalScore.toFixed(1)
    },
  },

  async mounted() {
    try {
      const res = await interviewApi.getInterview(this.interviewId)
      this.interview = res.data
    } catch (err) {
      this.error = err?.response?.data?.error || "Failed to load results"
    } finally {
      this.loading = false
    }
  },

  methods: {
    navigateBack() {
      const from = this.$route?.query?.from
      if (from) {
        this.$router.push(from)
      } else {
        this.$router.push("/dashboard")
      }
    },
    getScore(questionId) {
      return (
        this.interview?.scores?.find((s) => s.questionId === questionId)
          ?.score ?? "—"
      )
    },
    getRationale(questionId) {
      return (
        this.interview?.scores?.find((s) => s.questionId === questionId)
          ?.rationale ?? ""
      )
    },
    getResponse(questionId) {
      return (
        this.interview?.responses?.find((r) => r.questionId === questionId)
          ?.text ?? "(no answer)"
      )
    },
    getTimeSpent(questionId) {
      return (
        this.interview?.responses?.find((r) => r.questionId === questionId)
          ?.timeSpentSeconds ?? 0
      )
    },
    formatTime(seconds) {
      const m = Math.floor(seconds / 60)
      const s = seconds % 60
      return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    },
  },
}
</script>

<style scoped>
.score-badge {
  background: #212529;
  color: #fff;
  border-radius: 8px;
  padding: 0.75rem 1.25rem;
  min-width: 80px;
}
.score-value {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
}
.score-label {
  font-size: 0.8rem;
  opacity: 0.7;
}
.question-result {
  border-left: 3px solid #dee2e6;
  padding-left: 1rem;
}
.score-pill {
  font-size: 1.25rem;
  font-weight: 700;
  color: #212529;
  white-space: nowrap;
}
.score-pill span {
  font-size: 0.75rem;
  font-weight: 400;
  color: #6c757d;
}
</style>
